import {
  ChatAlt2Icon,
  ChatIcon,
  HeartIcon,
  SparklesIcon,
  SupportIcon,
} from '@heroicons/react/outline';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/**
 * Appearance → Chat bubble: where the bubble sits, its icon, colour and
 * label, with the bubble and its greeting as visitors see them.
 */
export default function BubbleStyles({
  brand,
  greeting,
}: {
  brand: DemoBrand;
  greeting: string;
}) {
  const { t } = useChannelsT();

  const icons = [SparklesIcon, ChatIcon, ChatAlt2Icon, HeartIcon, SupportIcon];
  const swatches = [
    brand.bg,
    'bg-[#4f46e5]',
    'bg-[#0f172a]',
    'bg-[#db2777]',
    'bg-[#ca8a04]',
  ];
  const corners = [
    'start-1.5 top-1.5',
    'end-1.5 top-1.5',
    'start-1.5 bottom-1.5',
    'end-1.5 bottom-1.5',
  ];

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-28'>
      <div
        className={clsxm(
          'rounded-[22px] p-5 text-start shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]',
          tone.surface,
          tone.line,
        )}
      >
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          {t('Chat bubble')}
        </p>
        <div className='mt-4 grid gap-5 sm:grid-cols-2'>
          <div>
            <p className={clsxm('text-xs font-medium', tone.quiet)}>
              {t('Position')}
            </p>
            <div className='mt-2 grid grid-cols-2 gap-2'>
              {corners.map((corner, index) => (
                <span
                  key={corner}
                  className={clsxm(
                    'relative h-12 rounded-lg ring-1 ring-inset',
                    index === 3
                      ? 'bg-primary-50 ring-primary-600 dark:bg-primary-400/10 dark:ring-primary-400'
                      : 'ring-[#0f0f28]/10 dark:ring-white/10',
                  )}
                >
                  <span
                    className={clsxm(
                      'absolute h-2.5 w-2.5 rounded-full',
                      corner,
                      index === 3 ? brand.bg : 'bg-[#c7c7d6] dark:bg-white/20',
                    )}
                  />
                </span>
              ))}
            </div>
          </div>
          <div className='space-y-4'>
            <div>
              <p className={clsxm('text-xs font-medium', tone.quiet)}>
                {t('Icon')}
              </p>
              <div className='mt-2 flex gap-1.5'>
                {icons.map((Icon, index) => (
                  <span
                    key={index}
                    className={clsxm(
                      'flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-inset',
                      index === 0
                        ? clsxm('text-white ring-transparent', brand.bg)
                        : clsxm(
                            'ring-[#0f0f28]/10 dark:ring-white/10',
                            tone.body,
                          ),
                    )}
                  >
                    <Icon className='h-4 w-4' />
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className={clsxm('text-xs font-medium', tone.quiet)}>
                {t('Color')}
              </p>
              <div className='mt-2 flex gap-1.5'>
                {swatches.map((swatch, index) => (
                  <span
                    key={swatch}
                    className={clsxm(
                      'h-7 w-7 rounded-full',
                      swatch,
                      index === 0 &&
                        'ring-2 ring-white ring-offset-2 ring-offset-[#0f0f28]/20 dark:ring-offset-white/40',
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className='mt-5 flex items-center justify-between gap-3 border-t border-black/[0.06] pt-4 dark:border-white/[0.06]'>
          <span className={clsxm('text-[13px]', tone.body)}>
            {t('Text in the chat bubble')}
          </span>
          <span
            className={clsxm(
              'rounded-lg px-3 py-1.5 text-[13px] font-medium ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10',
              tone.ink,
            )}
          >
            {t('Chat with us')}
          </span>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex flex-col items-end gap-2.5 sm:absolute sm:-bottom-2 sm:end-[-1rem] sm:mt-0',
        )}
        style={delay(450)}
      >
        <p className='max-w-[15rem] rounded-2xl rounded-ee-md bg-white px-4 py-2.5 text-start text-[13px] leading-snug text-[#10101c] shadow-[0_20px_40px_-18px_rgba(0,0,0,0.6)]'>
          {greeting}
        </p>
        <span
          className={clsxm(
            'flex items-center gap-2 rounded-full px-4 py-3 text-[14px] font-semibold text-white shadow-[0_18px_36px_-12px_rgba(0,0,0,0.6)]',
            brand.bg,
          )}
        >
          <SparklesIcon className='h-5 w-5' />
          {t('Chat with us')}
        </span>
      </div>
    </div>
  );
}
